import {
  AccountId,
  LowPriorityNotificationDetailType,
  NotificationType,
  OrgId,
  OrgInvoice,
  OrgInvoiceChild,
  OrgInvoiceParent,
  OrgInvoiceTypes,
  OrgPaymentInvoiceCredit,
  OrgPaymentInvoiceDefault,
  OrgPaymentInvoiceFailed,
  OrgPaymentInvoiceFailedECheck,
  OrgPaymentType,
  PaymentMethodType,
  PushNotificationSettingToRespect
} from "@ollie-sports/models";
import { NotificationGeneratorInfo__LowPriority, generateAndProcessNotifications } from "./notification.plumbing";
import { getAccountIdsFromPlayerBundleIdForPaymentNotifications } from "./playerBundle-helpers";
import { getUniversalHelpers } from "../../helpers";
import moment, { locale } from "moment";
import { dateFormatters, translate } from "@ollie-sports/i18n";
import {
  formatMoneyCentsToDollarCentPrettyString,
  getIndividualOrgInvoiceAmountDetails,
  ensureColorHasHashtag,
  getPaymentMethodToUse,
  getPaymentMethodString
} from "../../utils";
import { COLORS } from "../../constants";
import { playerBundle__server__getPrettyPlayerBundle } from "../playerBundle";
import { getDefaultPaymentAccount } from "../../compute/account.compute";
import { possiblyConvertNMIResponseCodeToTranslatedPrettyString } from "../../utils/payment-helpers";

type PropsFailed = {
  type: "failed";
  failType: "failed-scheduled";
  orgPayment: OrgPaymentInvoiceFailed;
  orgInvoice: OrgInvoiceChild;
};

type PropsFailedECheck = {
  type: "failed";
  failType: "failed-echeck";
  orgPayment: OrgPaymentInvoiceFailedECheck;
  orgInvoice: OrgInvoice;
};

type PropsSoonToBeCharged = {
  type: "soonToBeCharged";
  orgInvoiceData: {
    orgInvoiceChild: OrgInvoiceChild;
    orgInvoiceParent: OrgInvoiceParent;
    orgPaymentInvoiceCredits: OrgPaymentInvoiceCredit[];
  }[];
};

type PropsSuccessfulParentOrgInvoicePayment = {
  type: "successfulParentOrgInvoicePayment";
  orgInvoiceParent: OrgInvoiceParent;
  orgPayment: OrgPaymentInvoiceDefault;
  doesPaymentPlanExist: boolean;
  hasOutstandingAdditionalSteps: boolean;
};

type PropsSuccessfulChildOrgInvoicePayment = {
  type: "successfulChildOrgInvoicePayment";
  orgInvoiceChild: OrgInvoiceChild;
  orgPayment: OrgPaymentInvoiceDefault;
};
export type TriggerPaymentNotificationProps =
  | PropsFailed
  | PropsFailedECheck
  | PropsSoonToBeCharged
  | PropsSuccessfulParentOrgInvoicePayment
  | PropsSuccessfulChildOrgInvoicePayment;

export async function triggerForPaymentNotifications(p: TriggerPaymentNotificationProps) {
  // SERVER_ONLY_TOGGLE
  const { olliePipe, ollieFirestoreV2: h } = getUniversalHelpers();
  olliePipe.emitEvent({
    type: "metric-send-payment-notifications",
    payload: { type: "start", props: p }
  });
  const playerBundleIds =
    p.type == "failed"
      ? [p.orgPayment.playerBundleId]
      : p.type === "successfulParentOrgInvoicePayment"
      ? [p.orgInvoiceParent.playerBundleId]
      : p.type === "successfulChildOrgInvoicePayment"
      ? [p.orgInvoiceChild.playerBundleId]
      : p.orgInvoiceData.map(a => a.orgInvoiceChild.playerBundleId);
  const accountIdsToNotifyByPlayerBundleId = await getAccountIdsFromPlayerBundleIdForPaymentNotifications({ playerBundleIds });
  const today = moment().format("YYYY-MM-DD");

  if (p.type === "failed") {
    const accountIdsToNotify = accountIdsToNotifyByPlayerBundleId[p.orgPayment.playerBundleId];
    olliePipe.emitEvent({
      type: "metric-send-payment-notifications",
      payload: { type: "send-failed", accountIdsToNotify }
    });
    try {
      await generateAndProcessNotifications({
        data: accountIdsToNotify.map(accountId => {
          return {
            accountId,
            orgPayment: p.orgPayment,
            orgId: p.orgPayment.orgId,
            orgInvoice: p.orgInvoice,
            fetchAccount: true
          };
        }),
        generateFn: async ({ accountPrivate, data, org, account, orgSettings }) => {
          if (!org) {
            return null;
          }

          const ret: NotificationGeneratorInfo__LowPriority = {
            type: "lowPriorityNotification",
            notificationType: NotificationType.lowPriorityNotification,
            idempotencyKey: ["failed-payment", data.orgPayment.id].join("|")
          };

          const prettyFailureType =
            p.failType === "failed-scheduled"
              ? translate({
                  defaultMessage: "Failed Payment",
                  serverLocale: accountPrivate.communicationLocale
                })
              : translate({
                  defaultMessage: "Rejected Payment",
                  serverLocale: accountPrivate.communicationLocale
                });

          const amount = formatMoneyCentsToDollarCentPrettyString(
            p.orgPayment.amountCents + (p.orgPayment.lateFeeAmountCents ?? 0) + (p.orgPayment.processingFeeAmountCents ?? 0)
          );

          const body =
            p.failType === "failed-scheduled"
              ? translate(
                  {
                    defaultMessage: "A scheduled payment of {amount} failed. Click here to manage the payment.",
                    serverLocale: accountPrivate.communicationLocale
                  },
                  { amount }
                )
              : translate(
                  {
                    defaultMessage:
                      "An e-check payment of {amount} posted {date} was rejected by the bank. Click here to manage the payment.",
                    serverLocale: accountPrivate.communicationLocale
                  },
                  { amount, date: dateFormatters.m_d_yy_t_tt_a(p.orgPayment.createdAtMS, accountPrivate.communicationLocale) }
                );

          const title = `${org.shortName} - ${prettyFailureType}`;
          const emailSubject = `${org.name} - ${prettyFailureType}`;

          const failureReason =
            p.orgPayment.type === OrgPaymentType.invoiceFailedECheckPayment
              ? translate({ defaultMessage: "Reversed by the bank", serverLocale: accountPrivate.communicationLocale })
              : possiblyConvertNMIResponseCodeToTranslatedPrettyString({
                  locale: accountPrivate.communicationLocale,
                  responseCode: p.orgPayment.nmiPaymentResponseInfo.responseCode
                });

          const emailBodyTop =
            p.failType === "failed-scheduled"
              ? translate(
                  {
                    defaultMessage: "A scheduled payment of {amount} failed.",
                    serverLocale: accountPrivate.communicationLocale
                  },
                  { amount }
                )
              : translate(
                  {
                    defaultMessage: "An e-check payment of {amount} posted {date} was rejected by the bank.",
                    serverLocale: accountPrivate.communicationLocale
                  },
                  {
                    amount,
                    date: dateFormatters.m_d_yy_t_tt_a(p.orgPayment.createdAtMS, accountPrivate.communicationLocale)
                  }
                );

          const learnMoreMessage =
            p.failType === "failed-scheduled"
              ? translate({
                  defaultMessage:
                    "You can find more information on why the payment failed inside the Ollie app by going to Tools > Payments. You can also update your payment information in the Ollie app by going to Tools > Payment Methods.",
                  serverLocale: accountPrivate.communicationLocale
                })
              : translate({
                  defaultMessage:
                    "You may be able to find more information on why the payment failed by talking with your bank or going inside the Ollie app by going to Tools > Payments. You can also update your payment information in the Ollie app by going to Tools > Payment Methods.",
                  serverLocale: accountPrivate.communicationLocale
                });

          const emailBody = `${emailBodyTop}\n\n${
            p.orgPayment.paymentMethodSnapshot
              ? `<strong>${translate.common(accountPrivate.communicationLocale).PaymentMethod}:</strong> ${getPaymentMethodString(
                  {
                    locale: accountPrivate.communicationLocale,
                    paymentMethod: p.orgPayment.paymentMethodSnapshot,
                    includeName: true
                  }
                )}\n\n`
              : ""
          }${
            p.orgPayment.nmiPaymentResponseInfo.responseCode
              ? `<strong>${translate({
                  defaultMessage: "Failure Reason",
                  serverLocale: accountPrivate.communicationLocale
                })}:</strong> ${failureReason}\n\n`
              : ""
          }${learnMoreMessage}`;

          const parentInvoiceId =
            "parentOrgInvoiceId" in data.orgInvoice ? data.orgInvoice.parentOrgInvoiceId : data.orgInvoice.id;

          const routerPath = `main/tools/myPayments/${parentInvoiceId}`;

          const expireAtMS = moment().add(30, "days").valueOf();

          ret.lowPriorityNotificationDetail = {
            type: LowPriorityNotificationDetailType.failedAutoPayment,
            expireAtMS,
            routerPath,
            title,
            body
          };

          ret.pushNotificationData = {
            title,
            body,
            lowPriorityNotificationDetailType: LowPriorityNotificationDetailType.failedAutoPayment,
            pushNotificationSettingToRespect: PushNotificationSettingToRespect.ALWAYS_SEND,
            routerPath
          };

          ret.realTimeNotification = {
            e: expireAtMS
          };

          if (account) {
            ret.emailData = {
              type: "org",
              orgEmailPersonalization: {
                name: account.firstName,
                email: account.email,
                subject: emailSubject,
                message: emailBody
              },
              orgId: data.orgId,
              orgEmailReplyToEmailAddress: orgSettings?.registrationSettings?.defaultReplyToEmailAddress
              // orgEmailReplyToName: NATE TODO
            };
          }
          olliePipe.emitEvent({
            type: "metric-send-payment-notifications",
            payload: { type: "single-data-failed", ret }
          });

          return ret;
        }
      });
    } catch (e) {
      olliePipe.emitEvent({
        type: "metric-send-payment-notifications",
        payload: { type: "send-error-failed", accountIdsToNotify }
      });
    }
  } else if (p.type === "soonToBeCharged") {
    try {
      await generateAndProcessNotifications({
        data: p.orgInvoiceData.reduce(
          (acc, val) => {
            const accountIdsToNotify = accountIdsToNotifyByPlayerBundleId[val.orgInvoiceChild.playerBundleId];
            olliePipe.emitEvent({
              type: "metric-send-payment-notifications",
              payload: { type: "send-soon-to-be-charged", accountIdsToNotify, data: val }
            });
            accountIdsToNotify.forEach(accountId => {
              acc.push({
                accountId,
                orgId: val.orgInvoiceChild.orgId,
                fetchAccount: true,
                orgInvoiceChild: val.orgInvoiceChild,
                orgInvoiceParent: val.orgInvoiceParent,
                orgPaymentInvoiceCredits: val.orgPaymentInvoiceCredits
              });
            });
            return acc;
          },
          [] as {
            accountId: AccountId;
            orgInvoiceChild: OrgInvoiceChild;
            orgInvoiceParent: OrgInvoiceParent;
            orgId: OrgId;
            fetchAccount: true;
            orgPaymentInvoiceCredits: OrgPaymentInvoiceCredit[];
          }[]
        ),
        generateFn: async ({ accountPrivate, data, org, account, orgSettings }) => {
          if (!org) {
            return null;
          }

          const accountSecret = await h.AccountSecret.getDoc(accountPrivate.id);

          const defaultPaymentMethod = getDefaultPaymentAccount(Object.values(accountSecret?.paymentMethodsById ?? {}));

          if (!defaultPaymentMethod) {
            // Payment TODO
            return null;
          }

          const ret: NotificationGeneratorInfo__LowPriority = {
            type: "lowPriorityNotification",
            notificationType: NotificationType.lowPriorityNotification,
            idempotencyKey: ["scheduled-payment-reminder", data.orgInvoiceChild.id, today].join("|")
          };

          const orgInvoiceDetails = getIndividualOrgInvoiceAmountDetails({
            orgInvoice: data.orgInvoiceChild,
            orgPayments: data.orgPaymentInvoiceCredits,
            feeDetails: orgSettings?.customFeeDetails?.[defaultPaymentMethod.type],
            paymentMethodType: defaultPaymentMethod.type
          });

          const responsibleAccountSecret = await h.AccountSecret.getDoc(data.orgInvoiceChild.accountIdScheduledToPay);

          let paymentMethodString = "";
          if (responsibleAccountSecret && responsibleAccountSecret.paymentMethodsById) {
            const paymentMethodToUse = getPaymentMethodToUse(responsibleAccountSecret.paymentMethodsById);

            if (paymentMethodToUse) {
              paymentMethodString = getPaymentMethodString({
                locale: accountPrivate.communicationLocale,
                paymentMethod: paymentMethodToUse,
                includeName: true
              });
            }
          }

          const emailSubject = `${org.name} - ${translate({
            defaultMessage: "Auto Payment Reminder",
            serverLocale: accountPrivate.communicationLocale
          })}`;
          const emailBody = `${translate(
            {
              defaultMessage: "Your scheduled payment of {amount} will be automatically processed tomorrow.",
              serverLocale: accountPrivate.communicationLocale
            },
            {
              amount: formatMoneyCentsToDollarCentPrettyString(orgInvoiceDetails.totalRemainingAmountDueCents)
            }
          )}${
            paymentMethodString
              ? ` ${translate({
                  defaultMessage: "The following payment method will be charged:",
                  serverLocale: accountPrivate.communicationLocale
                })}\n\n${paymentMethodString}`
              : ""
          }\n\n${translate({
            defaultMessage: "You can view your upcoming payments inside the Ollie app by going to Tools > Payments.",
            serverLocale: accountPrivate.communicationLocale
          })}`;

          if (account) {
            ret.emailData = {
              type: "org",
              orgEmailPersonalization: {
                name: account.firstName,
                email: account.email,
                subject: emailSubject,
                message: emailBody
              },
              orgId: data.orgId,
              orgEmailReplyToEmailAddress: orgSettings?.registrationSettings?.defaultReplyToEmailAddress
              // orgEmailReplyToName: NATE TODO
            };
          }
          olliePipe.emitEvent({
            type: "metric-send-payment-notifications",
            payload: { type: "single-data-soon-to-be-charged", ret }
          });

          return ret;
        }
      });
    } catch (e) {
      olliePipe.emitEvent({
        type: "metric-send-payment-notifications",
        payload: { type: "error-send-soon-to-be-charged" }
      });
    }
  } else if (p.type === "successfulParentOrgInvoicePayment") {
    const accountIdsToNotify = accountIdsToNotifyByPlayerBundleId[p.orgPayment.playerBundleId];
    olliePipe.emitEvent({
      type: "metric-send-payment-notifications",
      payload: { type: "send-successful-parent", accountIdsToNotify }
    });
    const prettyPlayerBundle = await playerBundle__server__getPrettyPlayerBundle({
      id: p.orgInvoiceParent.playerBundleId
    });
    try {
      await generateAndProcessNotifications({
        data: accountIdsToNotify.map(accountId => {
          return {
            accountId,
            orgPayment: p.orgPayment,
            orgId: p.orgPayment.orgId,
            orgInvoiceParent: p.orgInvoiceParent,
            fetchAccount: true
          };
        }),
        generateFn: async ({ accountPrivate, data, org, account, orgSettings }) => {
          if (!org) {
            return null;
          }

          const ret: NotificationGeneratorInfo__LowPriority = {
            type: "lowPriorityNotification",
            notificationType: NotificationType.lowPriorityNotification,
            idempotencyKey: ["successful-parent-org-invoice-payment", data.orgPayment.id].join("|")
          };

          let emailSubject = `${org.name} - `;
          let emailBody = ``;

          const isZeroDollarPayment = p.orgPayment.amountCents === 0;
          const doesPaymentPlanExist = p.doesPaymentPlanExist;
          if (isZeroDollarPayment && !doesPaymentPlanExist) {
            // This shouldn't happen
            return null;
          }
          if (p.orgInvoiceParent.type === OrgInvoiceTypes.manual) {
            emailSubject =
              emailSubject +
              `${
                isZeroDollarPayment
                  ? translate({ defaultMessage: "Payment Plan Confirmation", serverLocale: accountPrivate.communicationLocale })
                  : translate({ defaultMessage: "Payment Confimation", serverLocale: accountPrivate.communicationLocale })
              }`;
            emailBody = `<div>${
              !isZeroDollarPayment
                ? `<p>${translate(
                    {
                      defaultMessage: "We have received your {amount} payment.",
                      serverLocale: accountPrivate.communicationLocale
                    },
                    { amount: `${formatMoneyCentsToDollarCentPrettyString(p.orgPayment.amountCents)}` }
                  )}</p>`
                : ""
            }${
              doesPaymentPlanExist
                ? `<p>${translate({
                    defaultMessage: "You can view the upcoming payments on your payment plan in the Ollie app on the Tools tab.",
                    serverLocale: accountPrivate.communicationLocale
                  })}</p>`
                : ""
            }</div>`;
          } else if (p.orgInvoiceParent.type === OrgInvoiceTypes.registration) {
            // Sending a notification already in orgInvoice__payOrgInvoiceAndPotentiallyGeneratePaymentPlanInstallmentInvoices
            return null;
          } else {
            return null;
          }

          if (account) {
            ret.emailData = {
              type: "org",
              orgEmailPersonalization: {
                name: account.firstName,
                email: account.email,
                subject: emailSubject,
                message: emailBody
              },
              orgId: data.orgId,
              orgEmailReplyToEmailAddress: orgSettings?.registrationSettings?.defaultReplyToEmailAddress
              // orgEmailReplyToName: NATE TODO
            };
          }
          olliePipe.emitEvent({
            type: "metric-send-payment-notifications",
            payload: { type: "single-data-successful-parent", ret }
          });

          return ret;
        }
      });
    } catch (e) {
      olliePipe.emitEvent({
        type: "metric-send-payment-notifications",
        payload: { type: "error-send-successful-parent", accountIdsToNotify }
      });
    }
  } else {
    const accountIdsToNotify = accountIdsToNotifyByPlayerBundleId[p.orgPayment.playerBundleId];
    olliePipe.emitEvent({
      type: "metric-send-payment-notifications",
      payload: { type: "send-successful-parent", accountIdsToNotify }
    });
    const prettyPlayerBundle = await playerBundle__server__getPrettyPlayerBundle({
      id: p.orgInvoiceChild.playerBundleId
    });
    try {
      await generateAndProcessNotifications({
        data: accountIdsToNotify.map(accountId => {
          return {
            accountId,
            orgPayment: p.orgPayment,
            orgId: p.orgPayment.orgId,
            orgInvoiceChild: p.orgInvoiceChild,
            fetchAccount: true
          };
        }),
        generateFn: async ({ accountPrivate, data, org, account, orgSettings }) => {
          if (!org) {
            return null;
          }

          const ret: NotificationGeneratorInfo__LowPriority = {
            type: "lowPriorityNotification",
            notificationType: NotificationType.lowPriorityNotification,
            idempotencyKey: ["successful-parent-org-invoice-payment", data.orgPayment.id].join("|")
          };

          let emailSubject = `${org.name} - `;
          let emailBody = ``;
          emailSubject =
            emailSubject +
            `${translate({ defaultMessage: "Payment Confimation", serverLocale: accountPrivate.communicationLocale })}`;
          emailBody = `<div>${`<p>${translate(
            {
              defaultMessage: "We have received your {amount} payment.",
              serverLocale: accountPrivate.communicationLocale
            },
            { amount: `${formatMoneyCentsToDollarCentPrettyString(p.orgPayment.amountCents)}` }
          )}</p>`}${`<p>${translate({
            defaultMessage: "You can view the all payment details in the Ollie app on the Tools tab.",
            serverLocale: accountPrivate.communicationLocale
          })}</p>`}</div>`;

          if (account) {
            ret.emailData = {
              type: "org",
              orgEmailPersonalization: {
                name: account.firstName,
                email: account.email,
                subject: emailSubject,
                message: emailBody
              },
              orgId: data.orgId,
              orgEmailReplyToEmailAddress: orgSettings?.registrationSettings?.defaultReplyToEmailAddress
              // orgEmailReplyToName: NATE TODO
            };
          }
          olliePipe.emitEvent({
            type: "metric-send-payment-notifications",
            payload: { type: "single-data-successful-parent", ret }
          });

          return ret;
        }
      });
    } catch (e) {
      olliePipe.emitEvent({
        type: "metric-send-payment-notifications",
        payload: { type: "error-send-successful-parent", accountIdsToNotify }
      });
    }
  }

  // SERVER_ONLY_TOGGLE
}
